import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getIsLaunchableCrm, groupByAsMap, isUUIDV4, Select } from '@aline/common';
import type { LaunchableCrm } from '@aline/common';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  ButtonBase,
  Divider,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import type { PopperProps } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColumnMenuContainer,
  gridFilterModelSelector,
  gridPinnedColumnsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import type {
  GridApiPremium,
  GridColDef,
  GridColumnMenuProps,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { ChevronDown, EyeOff, Layers, Pin, PinOff, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { matchSorter } from 'match-sorter';
import { useSnackbar } from 'notistack';
import { INTEGRATIONS_SECTION } from 'src/components/admin/integrations/integrations-nav-sections';
import { getIntegrationsSectionLink } from 'src/components/admin/integrations/integrations-section-spy';
import { AlineTextarea } from 'src/components/AlineTextarea';
import { AlineButton, AlineCheckbox, AlineTooltip, Spacer } from 'src/components/generic';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import {
  COLUMN_LOCK_EMPTY_LABEL,
  COLUMN_LOCK_LABEL,
  ExtractionQuestionInputsPopper,
  getColumnSpecificOpts,
  getLockAction,
  LockIcon,
  QuestionInputChips,
  questionTypeToReportColumnDataType,
  useColumnLockState,
  useLockConfirmation,
} from 'src/components/reports';
import type { LockableJob, LockAction } from 'src/components/reports';
import { RouterLink } from 'src/components/RouterLink';
import {
  ExtractionQuestionOptionsManager,
  ExtractionQuestionTypeIcon,
  ExtractionQuestionTypePopper,
} from 'src/editor';
import { useStable } from 'src/editor/helpers/useStable';
import { useIsOrgAdmin } from 'src/gql';
import type { OperationErrorContext } from 'src/gql/apollo-error-link';
import { Apollo } from 'src/gql/generated';
import { doNothing, useReportParams } from 'src/helpers';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import {
  getVisibleDocumentIds,
  patchExtractionColumnOptionInCache,
  useCancelExtractionRuns,
  useDeleteExtractionQuestion,
  useGetExtractionQuestionWithInputs,
  useRunExtractionJobsForDocuments,
  useSetQuestionInputsOptimistic,
  useStartReportExtraction,
  useViewportClamp,
} from 'src/hooks';
import { useUserIntegrations } from 'src/hooks/integrations';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { useRecheckCrmFields } from 'src/layouts/NotificationBell';
import { Sentry } from 'src/sentry';
import { appColors } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import { ColumnMenuPopper } from './ColumnMenuPopper';
import { DeleteAiQuestionButton } from './DeleteAiQuestionButton';
import { ExtractionModePopper } from './ExtractionModePopper';
import { InputChip, useInputChipIconStyles } from './QuestionInputChip';
import { RunStopButtonContent } from './run-stop-button-content';
import { useRunningQuestionIds } from './use-running-question-ids';

const QUESTION_HEADER_ICON_SIZE = 18;
const QUESTION_HEADER_CHEVRON_SIZE = 16;
const DELETE_PROPERTY_ICON_SIZE = 40;
const CRM_SYNC_ICON_SIZE = 16;
const CRM_SYNC_ICON_SLOT_WIDTH = 20;
const CRM_SETUP_POLL_MS = 5000;
const SETUP_RECHECK_DELAY_MS = 30_000;
const RECHECK_ICON_SIZE = 14;

export const ONLY_ADMINS_CAN_CHANGE_CRM_SYNC = 'Only Admins can change CRM sync.';

const CRM_SYNC_FLYOUT_MODIFIERS: PopperProps['modifiers'] = [
  { name: 'offset', options: { offset: [0, 12] } },
  { name: 'preventOverflow', options: { boundary: 'viewport', padding: 16 } },
];

const FIELD_STATUS_LABEL: Record<Apollo.CrmProvisionedFieldStatus, string> = {
  [Apollo.CrmProvisionedFieldStatus.Failed]: 'Failed',
  [Apollo.CrmProvisionedFieldStatus.Live]: 'Synced',
  [Apollo.CrmProvisionedFieldStatus.Pending]: 'Needs access',
};

type CrmSyncedField = Apollo.GetCrmProvisionedFieldsQuery['crmProvisionedFields'][number];

type CrmSyncSelection = {
  readonly syncToCrm: boolean;
  readonly syncToCrmIntegrations: readonly Apollo.Integration[];
};

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  deleteIcon: {
    marginRight: theme.spacing(2),
    minWidth: 'unset',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    width: 400,
  },
  pinButton: {
    opacity: 0,
    transition: 'opacity 0.2s, background-color 0.2s',
  },
  pinButtonPinned: {
    '&:hover': {
      backgroundColor: theme.palette.grey[900],
    },
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    opacity: 1,
  },
  skeletonButton: {
    borderRadius: 16,
  },
  skeletonMenuButton: {
    alignItems: 'baseline',
    borderRadius: 8,
    display: 'flex',
    flexGrow: 1,
    height: 32,
    justifyContent: 'left',
    padding: theme.spacing(0, 1),
  },
  sparkleIcon: {
    marginRight: theme.spacing(1),
  },
}));

export type ColumnHeaderMenuExtraProps = {
  popperAnchor?: 'left-start' | 'right-start';
};
type ColumnHeaderMenuProps = GridColumnMenuProps & ColumnHeaderMenuExtraProps;

export function ColumnHeaderMenu(props: ColumnHeaderMenuProps): JSX.Element {
  const { colDef: currentColumn } = props;
  const isAIField = isUUIDV4(currentColumn.field);
  const { maxHeight, setNode } = useViewportClamp<HTMLUListElement>();

  if (isAIField)
    return (
      <GridColumnMenuContainer
        ref={setNode}
        style={{ maxHeight, overflowY: 'auto', paddingBottom: 0, paddingTop: 0 }}
        {...props}
      >
        <QuestionColumnHeaderMenu {...props} />
      </GridColumnMenuContainer>
    );

  return (
    <GridColumnMenuContainer ref={setNode} style={{ maxHeight, overflowY: 'auto' }} {...props}>
      <SystemColumnHeaderMenu {...props} />
    </GridColumnMenuContainer>
  );
}

export function SystemColumnHeaderMenu({ colDef }: GridColumnMenuProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const pinnedColumns = gridPinnedColumnsSelector(apiRef);
  const isPinnedLeft = pinnedColumns.left?.includes(colDef.field) ?? false;

  // Don't allow unpinning the checkbox column
  const canTogglePin = colDef.field !== GRID_CHECKBOX_SELECTION_FIELD;

  const handlePinClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isPinnedLeft) {
      apiRef.current.setPinnedColumns({
        ...pinnedColumns,
        left: pinnedColumns.left?.filter(field => field !== colDef.field),
      });
      return;
    }

    apiRef.current.setPinnedColumns({
      ...pinnedColumns,
      left: [...(pinnedColumns.left ?? []), colDef.field],
    });
  };

  return (
    <>
      {canTogglePin && (
        <MenuItem onClick={handlePinClick}>
          <ListItemIcon className={classes.deleteIcon}>
            {isPinnedLeft ? (
              <PinOff size={QUESTION_HEADER_ICON_SIZE} />
            ) : (
              <Pin size={QUESTION_HEADER_ICON_SIZE} />
            )}
          </ListItemIcon>
          <ListItemText>{isPinnedLeft ? 'Unpin column' : 'Pin column'}</ListItemText>
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          apiRef.current.setColumnVisibility(colDef.field, false);
        }}
      >
        <ListItemIcon className={classes.deleteIcon}>
          <EyeOff size={QUESTION_HEADER_ICON_SIZE} />
        </ListItemIcon>
        <ListItemText>Hide property</ListItemText>
      </MenuItem>
    </>
  );
}

export interface ColumnHeaderQuestion extends Pick<
  Apollo.ExtractionQuestion,
  | 'id'
  | 'inputFileFields'
  | 'questionText'
  | 'title'
  | '__typename'
  | 'type'
  | 'variant'
  | 'options'
  | 'mode'
> {
  inputs: Pick<Apollo.ExtractionQuestion, 'id' | 'title' | '__typename'>[];
  syncToCrm?: boolean;
  syncToCrmIntegrations?: Apollo.Integration[];
}

interface QuestionColumnHeaderMenuBodyProps {
  colDef: GridColDef;
  context:
    | {
        apiRef: React.RefObject<GridApiPremium | null>;
        /**
         * Live grid filterModel — must be supplied by the caller via
         * `useGridSelector` so the lock affordances react to filter changes.
         */
        filterModel: GridFilterModel | undefined;
        reportId: string;
        type: 'report';
      }
    | {
        documentId: string;
        extractionJob: (Pick<Apollo.ExtractionJob, 'id' | '__typename'> & LockableJob) | null;
        isRunning: boolean;
        type: 'document';
      };
  question: ColumnHeaderQuestion;
  onClose?: () => void;
  popperAnchor?: 'left-start' | 'right-start';
  showHideButton?: boolean;
}

interface QuestionFormState {
  questionText: string;
  questionTitle: string;
}

type ActiveMenu = 'crmSync' | 'inputs' | 'type' | 'mode' | null;

export function QuestionColumnHeaderMenu({
  colDef,
  popperAnchor = 'right-start',
}: ColumnHeaderMenuProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const { reportId } = useReportParams();
  const { extractionQuestion, loading } = useGetExtractionQuestionWithInputs(colDef.field);

  if (loading || !extractionQuestion)
    return (
      <div className={classes.paper}>
        <div className={classes.header}>
          <Skeleton className={classes.skeletonMenuButton} height={32} />
          <Skeleton height={32} variant="circular" width={32} />
        </div>
        <Divider />
        <div className={classes.container}>
          <Skeleton height={100} variant="rectangular" width="100%" />
        </div>
        <Divider />
        <div className={classes.container}>
          <Skeleton className={classes.skeletonMenuButton} height={32} />
        </div>
        <Divider />
        <div className={classes.container}>
          <Skeleton className={classes.skeletonButton} height={48} />
        </div>
      </div>
    );

  return (
    <QuestionColumnHeaderMenuBody
      colDef={colDef}
      context={{ apiRef, filterModel, reportId, type: 'report' }}
      popperAnchor={popperAnchor}
      question={extractionQuestion}
    />
  );
}

interface QuestionMenuBodyStyleProps {
  activeMenu: ActiveMenu;
}

export const questionMenuItemBorderRadius = 8;
const useQuestionMenuBodyStyles = makeStyles<QuestionMenuBodyStyleProps>()(
  (theme, { activeMenu }) => ({
    buttonRow: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    buttonTitle: {
      ...theme.appTypography.fieldLabel,
    },
    buttonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
    crmSyncMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'crmSync' ? theme.appColors.hoverBackground : undefined,
    },
    deleteBody: {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: theme.spacing(1),
      width: 400,
    },
    deleteButtonContainer: {
      display: 'flex',
      flexWrap: 'nowrap',
      gap: theme.spacing(2),
      width: '100%',
    },
    deletePropertyIcon: {
      marginTop: 32,
    },
    inputsMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'inputs' ? theme.appColors.hoverBackground : undefined,
    },
    menuButton: {
      alignItems: 'center',
      borderRadius: questionMenuItemBorderRadius,
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'left',
      minHeight: 32,
      padding: theme.spacing(0.5, 1),
      width: '100%',
    },
    modeMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'mode' ? theme.appColors.hoverBackground : undefined,
    },
    modeText: {
      fontSize: 12,
    },
    propertyName: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      borderRadius: questionMenuItemBorderRadius,
      flexGrow: 1,
      height: 32,
      padding: theme.spacing(0, 1),
    },
    tooltipWrapper: {
      display: 'flex',
      width: '100%',
    },
    typeMenuButton: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      backgroundColor: activeMenu === 'type' ? theme.appColors.hoverBackground : undefined,
    },
    typeText: {
      fontSize: 12,
    },
  })
);

interface SyncToCrmRowProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  popperAnchor: 'left-start' | 'right-start';
  questionId: string;
}

const SyncToCrmRow = React.memo(function SyncToCrmRow({
  isAvailable,
  ...props
}: SyncToCrmRowProps & { isAvailable: boolean }): JSX.Element | null {
  const { canChangeCrmSync, hasConnectedCrms, isConnectionsLoading } = useCrmSync();
  if (!isAvailable || isConnectionsLoading) return null;
  if (!hasConnectedCrms)
    return (
      <SyncToCrmEmptyState
        isOpen={props.isOpen}
        popperAnchor={props.popperAnchor}
        onClose={props.onClose}
        onOpen={props.onOpen}
      />
    );

  return <SyncToCrmControl canChange={canChangeCrmSync} {...props} />;
});

export function QuestionColumnHeaderMenuBody({
  colDef,
  context,
  onClose = doNothing,
  popperAnchor = 'right-start',
  question,
  showHideButton = true,
}: QuestionColumnHeaderMenuBodyProps): JSX.Element {
  const { classes } = useStyles();
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const [body, setBody] = useState<'question' | 'delete'>('question');
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const [promptEl, setPromptEl] = useState<HTMLTextAreaElement | null>(null);
  const { classes: questionMenuBodyClasses } = useQuestionMenuBodyStyles({ activeMenu });
  const { deleteExtractionQuestion, loading: deletingExtractionQuestion } =
    useDeleteExtractionQuestion();
  const { isOrgAdmin, loading: loadingCurrentUser } = useIsOrgAdmin();
  const canDeleteAiQuestion = isOrgAdmin && !loadingCurrentUser;
  const { loading: startingExtraction, startExtractionJobsForDocuments } =
    useRunExtractionJobsForDocuments();
  const [typeButtonEl, setTypeButtonEl] = useState<HTMLElement | null>(null);
  const [inputsButtonEl, setInputsButtonEl] = useState<HTMLElement | null>(null);
  const [modeButtonEl, setModeButtonEl] = useState<HTMLElement | null>(null);
  const { updateQuestionTypeOptimistic } = useUpdateQuestionTypeOptimistic();
  const { updateQuestionModeOptimistic } = useUpdateQuestionModeOptimistic();
  const [updateExtractionQuestion] = Apollo.useUpdateExtractionQuestionMutation();
  const { setQuestionInputsOptimistic } = useSetQuestionInputsOptimistic(question.id);
  const debouncedUpdateExtractionQuestion = useDebouncedCallback(updateExtractionQuestion, 500);
  const { loading: startingColExtraction, startReportExtraction } = useStartReportExtraction();
  const { cancelExtractionRuns, loading: stoppingExtraction } = useCancelExtractionRuns();
  const client = useApolloClient();
  const [state, setState] = useState<QuestionFormState>({
    questionText: question.questionText,
    questionTitle: question.title,
  });
  const { extractionQuestion } = useGetExtractionQuestionWithInputs(question.id);
  const syncedCrmNames = getSyncedCrmNames(extractionQuestion ?? question);
  const [renameWarning, setRenameWarning] = useState<{ isAcknowledged: boolean; isOpen: boolean }>({
    isAcknowledged: false,
    isOpen: false,
  });
  const isRenameLocked = Boolean(syncedCrmNames) && !renameWarning.isAcknowledged;
  const reportId = context.type === 'report' ? context.reportId : null;
  const applyTitleChange = async (title: string) => {
    setState(prev => ({ ...prev, questionTitle: title }));
    if (context.type === 'report')
      context.apiRef.current?.updateColumns([
        {
          ...colDef,
          headerName: title,
        },
      ]);
    patchExtractionColumnOptionInCache(client.cache, {
      patch: { displayName: title },
      questionId: question.id,
      reportId,
    });
    await debouncedUpdateExtractionQuestion({
      refetchQueries: [
        {
          query: Apollo.GetExtractionQuestionWithDependenciesDocument,
          variables: {
            id: question.id,
          } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
        },
      ],
      variables: {
        input: {
          props: {
            title: title.trim(),
          },
          questionId: question.id,
        },
      },
    });
  };
  const liveFilterModel = context.type === 'report' ? context.filterModel : undefined;
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const handleLockColumn = useLockColumnHandler({
    filterModel: liveFilterModel,
    questionId: question.id,
    reportId,
  });
  const columnLockState = useColumnLockState(reportId, question.id, {
    fetchPolicy: 'cache-and-network',
    filterModel: liveFilterModel,
    nextFetchPolicy: 'cache-first',
  });
  const isAiMode = question.mode === Apollo.ExtractionQuestionMode.Ai;
  const { runningQuestionIds } = useRunningQuestionIds({
    filterModel: liveFilterModel,
    questionIds: [question.id],
    reportId,
    skip: !isAiMode,
  });
  const isQuestionRunning =
    context.type === 'report' ? runningQuestionIds.includes(question.id) : context.isRunning;

  const pinnedColumns =
    context.type === 'report' ? gridPinnedColumnsSelector(context.apiRef) : { left: [], right: [] };
  const isPinnedLeft =
    context.type === 'report' && (pinnedColumns.left?.includes(colDef.field) ?? false);

  useEffect(() => {
    if (inputEl) inputEl.select();
  }, [inputEl]);

  const closeMenu = () => {
    onClose();
    if (context.type === 'report') context.apiRef.current?.hideColumnMenu();
  };

  const handlePinClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (context.type !== 'report') {
      return;
    }

    if (isPinnedLeft) {
      context.apiRef.current?.setPinnedColumns({
        ...pinnedColumns,
        left: pinnedColumns.left?.filter(field => field !== colDef.field),
      });
      return;
    }

    context.apiRef.current?.setPinnedColumns({
      ...pinnedColumns,
      left: [...(pinnedColumns.left ?? []), colDef.field],
    });
  };

  const handleExtraction = async () => {
    if (question.mode === Apollo.ExtractionQuestionMode.Manual) {
      closeMenu();
      return;
    }

    switch (context.type) {
      case 'report': {
        await startReportExtraction({
          variables: {
            input: {
              filterModel: liveFilterModel,
              filterTimeZone,
              questionId: question.id,
              reportId: context.reportId,
              visibleDocumentIds: getVisibleDocumentIds(context.apiRef.current),
            },
          },
        });
        closeMenu();
        break;
      }
      case 'document': {
        await startExtractionJobsForDocuments({
          variables: {
            input: {
              documentIds: [context.documentId],
              // force: true needed for document context to ensure play button always re-runs specific question.
              force: true,
              questionIds: [question.id],
            },
          },
        });
        closeMenu();
      }
    }
  };

  const handleStopExtraction = async () => {
    await cancelExtractionRuns(getStopRunsInput(), {
      clearRunningJobsOn:
        context.type === 'document'
          ? { documentId: context.documentId, questionIds: [question.id] }
          : undefined,
      showResult: true,
    });
    closeMenu();
  };

  function getStopRunsInput(): Apollo.CancelExtractionRunsInput {
    switch (context.type) {
      case 'report':
        return {
          documentIds: [],
          filterModel: liveFilterModel,
          filterTimeZone,
          questionIds: [question.id],
          reportId: context.reportId,
          runAllDocs: true,
        };
      case 'document':
        return { documentIds: [context.documentId], questionIds: [question.id] };
    }
  }

  const editorLockAction = context.type === 'report' ? getLockAction(columnLockState) : null;
  const lockTitle = editorLockAction
    ? COLUMN_LOCK_LABEL[editorLockAction]
    : COLUMN_LOCK_EMPTY_LABEL;

  const handleLockClick = async (action: LockAction) => {
    handleLockColumn(action);
  };

  switch (body) {
    case 'question': {
      return (
        <>
          <div className={classes.paper}>
            <div className={classes.header}>
              <InputBase
                className={questionMenuBodyClasses.propertyName}
                data-cy={Select.report.aiPropertyNameInput}
                inputRef={setInputEl}
                placeholder="Type property name..."
                readOnly={isRenameLocked}
                value={state.questionTitle}
                onChange={e => void applyTitleChange(e.target.value)}
                onCut={e => {
                  if (!isRenameLocked) return;
                  e.preventDefault();
                  setRenameWarning({ isAcknowledged: false, isOpen: true });
                }}
                onKeyDown={e => {
                  if ((e.key === 'Tab' && !e.shiftKey) || e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    promptEl?.focus();
                    return;
                  }
                  const isEdit = e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete';
                  if (isRenameLocked && isEdit && !e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                    setRenameWarning({ isAcknowledged: false, isOpen: true });
                  }
                }}
                onPaste={e => {
                  if (!isRenameLocked) return;
                  e.preventDefault();
                  setRenameWarning({ isAcknowledged: false, isOpen: true });
                }}
              />
              {context.type === 'report' && (
                <AlineTooltip
                  arrow
                  placement="top"
                  title={isPinnedLeft ? 'Unpin column' : 'Pin column'}
                >
                  <div>
                    <IconButton size="small" onClick={handlePinClick}>
                      {isPinnedLeft ? (
                        <PinOff size={QUESTION_HEADER_ICON_SIZE} />
                      ) : (
                        <Pin size={QUESTION_HEADER_ICON_SIZE} />
                      )}
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {showHideButton && (
                <AlineTooltip arrow placement="top" title="Hide property">
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (context.type === 'report')
                          context.apiRef.current?.setColumnVisibility(colDef.field, false);
                        closeMenu();
                      }}
                    >
                      <EyeOff size={QUESTION_HEADER_ICON_SIZE} />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {context.type === 'report' && editorLockAction && (
                <AlineTooltip arrow placement="top" title={lockTitle}>
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleLockClick(editorLockAction);
                        closeMenu();
                      }}
                    >
                      <LockIcon
                        action={editorLockAction}
                        iconProps={{ size: QUESTION_HEADER_ICON_SIZE }}
                      />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {context.type === 'report' && !editorLockAction && (
                <AlineTooltip arrow placement="top" title={COLUMN_LOCK_EMPTY_LABEL}>
                  <div>
                    <IconButton disabled size="small">
                      <LockIcon action="empty" iconProps={{ size: QUESTION_HEADER_ICON_SIZE }} />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              <DeleteAiQuestionButton
                canDelete={canDeleteAiQuestion}
                onDelete={() => setBody('delete')}
              />
            </div>
            <Divider />
            <div className={classes.container}>
              <AlineTextarea
                ref={setPromptEl}
                data-cy={Select.report.promptText}
                maxRows={5}
                minRows={5}
                placeholder="Type an AI prompt (e.g., What is the effective date)..."
                value={state.questionText}
                onChange={async e => {
                  setState(prev => ({ ...prev, questionText: e.target.value }));
                  await debouncedUpdateExtractionQuestion({
                    refetchQueries: [
                      {
                        query: Apollo.GetExtractionQuestionWithDependenciesDocument,
                        variables: {
                          id: question.id,
                        } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
                      },
                    ],
                    variables: {
                      input: {
                        props: {
                          questionText: e.target.value.trim(),
                        },
                        questionId: question.id,
                      },
                    },
                  });
                }}
                onKeyDown={e => {
                  if (e.key === 'Tab' && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    inputEl?.focus();
                  }
                }}
              />
            </div>
            <Divider />
            <div className={questionMenuBodyClasses.buttonsContainer}>
              <ButtonBase
                ref={setModeButtonEl}
                className={clsx(
                  questionMenuBodyClasses.menuButton,
                  questionMenuBodyClasses.modeMenuButton
                )}
                onClick={() => setActiveMenu('mode')}
              >
                <Typography className={questionMenuBodyClasses.buttonTitle}>Mode</Typography>
                <Spacer />
                <Typography className={questionMenuBodyClasses.modeText}>
                  {question.mode === Apollo.ExtractionQuestionMode.Ai ? 'AI' : 'Manual Entry'}
                </Typography>
                <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
              </ButtonBase>
              <ButtonBase
                ref={setTypeButtonEl}
                className={clsx(
                  questionMenuBodyClasses.menuButton,
                  questionMenuBodyClasses.typeMenuButton
                )}
                onClick={() => setActiveMenu('type')}
              >
                <Typography className={questionMenuBodyClasses.buttonTitle}>Output</Typography>
                <Spacer />
                <ExtractionQuestionTypeIcon editing type={question.type} />
                <Typography className={questionMenuBodyClasses.typeText}>
                  {question.type}
                </Typography>
                <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
              </ButtonBase>
              <ExtractionQuestionOptionsManager question={question} />
              {isAiMode && (
                <ButtonBase
                  ref={setInputsButtonEl}
                  className={clsx(
                    questionMenuBodyClasses.menuButton,
                    questionMenuBodyClasses.inputsMenuButton
                  )}
                  onClick={() => setActiveMenu('inputs')}
                >
                  <Typography className={questionMenuBodyClasses.buttonTitle}>Inputs</Typography>
                  <Spacer />
                  <QuestionInputChips
                    inputFileFields={question.inputFileFields ?? []}
                    inputs={question.inputs}
                  />
                  <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
                </ButtonBase>
              )}
              <SyncToCrmRow
                isAvailable={question.syncToCrm !== undefined}
                isOpen={activeMenu === 'crmSync'}
                popperAnchor={popperAnchor}
                questionId={question.id}
                onClose={() => setActiveMenu(null)}
                onOpen={() => setActiveMenu('crmSync')}
              />
            </div>
            {isAiMode && (
              <>
                <Divider />
                <div className={classes.container}>
                  <div className={questionMenuBodyClasses.buttonRow}>
                    <AlineButton
                      fullWidth
                      color="primary"
                      data-cy={Select.report.generateWithAlineAIButton}
                      loading={startingColExtraction || startingExtraction || stoppingExtraction}
                      variant={isQuestionRunning ? 'outlined' : 'contained'}
                      onClick={isQuestionRunning ? handleStopExtraction : handleExtraction}
                    >
                      <RunStopButtonContent
                        iconSize={QUESTION_HEADER_ICON_SIZE}
                        isRunning={isQuestionRunning}
                      />
                    </AlineButton>
                  </div>
                </div>
              </>
            )}
            <ExtractionModePopper
              popperProps={{
                anchorEl: modeButtonEl,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 12],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 16,
                    },
                  },
                ],
                open: activeMenu === 'mode',
                placement: popperAnchor,
              }}
              question={question}
              onChangeMode={async mode => {
                setActiveMenu(null);
                await updateQuestionModeOptimistic(question.id, mode);
              }}
              onClose={() => setActiveMenu(null)}
            />
            <ExtractionQuestionTypePopper
              popperProps={{
                anchorEl: typeButtonEl,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 12],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 16,
                    },
                  },
                ],
                open: activeMenu === 'type',
                placement: popperAnchor,
              }}
              selectedType={question.type}
              onChangeType={async type => {
                setActiveMenu(null);
                const newDataType = questionTypeToReportColumnDataType(type);
                if (context.type === 'report') {
                  const columnSpecificOpts = getColumnSpecificOpts({
                    __typename: 'ExtractionReportColumn',
                    dataType: newDataType,
                    displayName: colDef.headerName ?? '',
                    isGroupable: true,
                    questionId: question.id,
                  });
                  context.apiRef.current?.updateColumns([
                    {
                      ...colDef,
                      ...columnSpecificOpts,
                    },
                  ]);
                }
                patchExtractionColumnOptionInCache(client.cache, {
                  patch: { dataType: newDataType },
                  questionId: question.id,
                  reportId,
                });
                await updateQuestionTypeOptimistic(question.id, type);
              }}
              onClose={() => setActiveMenu(null)}
            />
            <ExtractionQuestionInputsPopper
              apiRef={context.type === 'report' ? context.apiRef : null}
              inputFileFields={question.inputFileFields ?? []}
              inputQuestionIds={question.inputs.map(input => input.id)}
              popperProps={{
                anchorEl: inputsButtonEl,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 12],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 16,
                    },
                  },
                ],
                open: activeMenu === 'inputs',
                placement: popperAnchor,
              }}
              question={question}
              onChangeSelectedInputs={async ({ inputFileFields, inputQuestionIds }) => {
                await setQuestionInputsOptimistic({
                  variables: {
                    input: {
                      inputFileFields,
                      inputQuestionIds,
                      questionId: question.id,
                    },
                  },
                });
              }}
              onClose={() => setActiveMenu(null)}
            />
          </div>
          <ConfirmationDialog
            TransitionProps={{
              onExited: () => {
                if (renameWarning.isAcknowledged) inputEl?.select();
              },
            }}
            message={`This property syncs to ${syncedCrmNames}, so its field there gets the new name too.`}
            open={renameWarning.isOpen}
            primaryText="Continue"
            title="Renaming also renames the CRM field"
            variant={ConfirmationDialogVariant.Warn}
            onClickPrimary={() => setRenameWarning({ isAcknowledged: true, isOpen: false })}
            onClose={() => setRenameWarning(prev => ({ ...prev, isOpen: false }))}
          />
        </>
      );
    }
    case 'delete': {
      return (
        <div className={questionMenuBodyClasses.deleteBody}>
          <Trash2
            className={questionMenuBodyClasses.deletePropertyIcon}
            size={DELETE_PROPERTY_ICON_SIZE}
          />
          <Typography align="center" variant="subtitle1">
            Are you sure you want to delete this AI property?
          </Typography>
          <Typography align="center" color="textSecondary">
            Deleting will remove the AI property from all reports. This action cannot be undone.
          </Typography>
          {Boolean(syncedCrmNames) && (
            <Typography align="center" color="error">
              This property syncs to {syncedCrmNames}. Deleting it also deletes its field there,
              with every value it holds.
            </Typography>
          )}
          <div className={questionMenuBodyClasses.deleteButtonContainer}>
            <AlineButton
              fullWidth
              color="primary"
              variant="outlined"
              onClick={() => setBody('question')}
            >
              Go Back
            </AlineButton>
            <AlineButton
              fullWidth
              color="primary"
              loading={deletingExtractionQuestion}
              variant="contained"
              onClick={async () => {
                const { data } = await deleteExtractionQuestion(colDef.field);
                if (data) {
                  closeMenu();
                  if (context.type === 'report')
                    context.apiRef.current?.setColumnVisibility(colDef.field, false);
                }
              }}
            >
              Delete
            </AlineButton>
          </div>
        </div>
      );
    }
  }
}

void gql`
  mutation UpdateQuestionType($input: UpdateExtractionQuestionInput!) {
    updateExtractionQuestion(input: $input) {
      __typename
      id
      contentHash
      type
    }
  }
`;

function useUpdateQuestionTypeOptimistic() {
  const [updateExtractionQuestionType] = Apollo.useUpdateQuestionTypeMutation();
  const updateQuestionTypeOptimistic = useCallback(
    async (questionId: string, type: Apollo.ExtractionQuestionType) => {
      await updateExtractionQuestionType({
        optimisticResponse: {
          __typename: 'Mutation',
          updateExtractionQuestion: {
            __typename: 'ExtractionQuestion',
            contentHash: uuidv4(),
            id: questionId,
            type,
          },
        },
        refetchQueries: [
          {
            query: Apollo.GetExtractionQuestionWithDependenciesDocument,
            variables: {
              id: questionId,
            } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
          },
        ],
        variables: {
          input: { props: { type }, questionId },
        },
      });
    },
    [updateExtractionQuestionType]
  );

  return { updateQuestionTypeOptimistic };
}

void gql`
  mutation UpdateQuestionMode($input: UpdateExtractionQuestionInput!) {
    updateExtractionQuestion(input: $input) {
      __typename
      id
      mode
    }
  }
`;

function useUpdateQuestionModeOptimistic() {
  const [updateExtractionQuestionMode] = Apollo.useUpdateQuestionModeMutation();
  const updateQuestionModeOptimistic = useCallback(
    async (questionId: string, mode: Apollo.ExtractionQuestionMode) => {
      await updateExtractionQuestionMode({
        optimisticResponse: {
          __typename: 'Mutation',
          updateExtractionQuestion: {
            __typename: 'ExtractionQuestion',
            id: questionId,
            mode,
          },
        },
        variables: {
          input: { props: { mode }, questionId },
        },
      });
    },
    [updateExtractionQuestionMode]
  );

  return { updateQuestionModeOptimistic };
}

/**
 * Shared lock-column handler for AI property and standard property header
 * menus. Wraps the toggle mutation in the lock-confirmation modal and
 * normalizes refetch / error / snackbar handling across both call sites.
 */
function useLockColumnHandler({
  filterModel,
  questionId,
  reportId,
}: {
  /**
   * Live grid filterModel (subscribed via `useGridSelector` in the caller) so
   * the mutation scopes to the rows the user is actually looking at.
   */
  filterModel: GridFilterModel | undefined;
  questionId: string;
  reportId: string | null | undefined;
}): (action: LockAction) => void {
  const [toggleExtractionJobsLock] = Apollo.useToggleExtractionJobsLockMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { requestLockConfirmation } = useLockConfirmation();

  return (action: LockAction) => {
    if (!reportId) return;
    requestLockConfirmation({
      action,
      onConfirm: async () => {
        try {
          await toggleExtractionJobsLock({
            refetchQueries: [
              {
                query: Apollo.GetQuestionLockStatusDocument,
                variables: { questionId, reportId },
              },
            ],
            variables: {
              input: {
                filterModel: filterModel ?? null,
                isLocked: action === 'lock',
                questionId,
                reportId,
                runAllJobs: true,
              },
            },
          });
        } catch (err) {
          Sentry.captureException(err, { tags: { feature: 'report-column-lock' } });
          enqueueSnackbar('Failed to update column lock. Please try again.', { variant: 'error' });
        }
      },
      target: 'column',
    });
  };
}

const useCrmSyncStyles = makeStyles()(theme => ({
  checkbox: {
    padding: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  connectItem: {
    color: theme.palette.primary.main,
  },
  icon: {
    flexShrink: 0,
    height: CRM_SYNC_ICON_SIZE,
    objectFit: 'contain',
    width: CRM_SYNC_ICON_SIZE,
  },
  iconSlot: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    width: CRM_SYNC_ICON_SLOT_WIDTH,
  },
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },
  recheck: {
    marginRight: theme.spacing(-0.5),
    padding: theme.spacing(0.5),
  },
  sectionLabel: {
    fontWeight: 600,
  },
  selectionText: {
    fontSize: 12,
  },
  status: {
    fontSize: 12,
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
  statusFailed: {
    color: theme.palette.error.main,
  },
  statusLive: {
    color: appColors.connectedCheck,
  },
  statusPending: {
    color: theme.palette.warning.main,
  },
  statusSettingUp: {
    color: theme.palette.text.secondary,
  },
}));

function useCrmSync() {
  const { isOrgAdmin, loading: loadingCurrentUser } = useIsOrgAdmin();
  const canChangeCrmSync = isOrgAdmin && !loadingCurrentUser;
  const { callableConnections, initialLoading } = useUserIntegrations();
  const connectedCrms = useMemo(
    () =>
      callableConnections
        .map(connection => connection.integration)
        .filter((integration): integration is LaunchableCrm => getIsLaunchableCrm(integration)),
    [callableConnections]
  );
  const hasConnectedCrms = connectedCrms.length > 0;
  /**
   * Non-admins still see the row. The field list is admin-only on the server,
   * so skip it unless this user can change the sync.
   */
  const { data, loading, startPolling, stopPolling } = Apollo.useGetCrmProvisionedFieldsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !canChangeCrmSync || !hasConnectedCrms,
  });

  return useMemo(() => {
    const fields = data?.crmProvisionedFields ?? [];
    return {
      canChangeCrmSync,
      connectedCrms,
      fieldsByQuestionId: groupByAsMap(field => field.questionId, fields),
      hasConnectedCrms,
      isConnectionsLoading: initialLoading,
      loading,
      startPolling,
      stopPolling,
    };
  }, [
    canChangeCrmSync,
    connectedCrms,
    data,
    hasConnectedCrms,
    initialLoading,
    loading,
    startPolling,
    stopPolling,
  ]);
}

function useCrmSyncSelection(questionId: string, isOpen: boolean) {
  const { enqueueSnackbar } = useSnackbar();
  const { extractionQuestion } = useGetExtractionQuestionWithInputs(questionId);
  const { connectedCrms, fieldsByQuestionId, startPolling, stopPolling } = useCrmSync();
  const fieldByCrm = useMemo((): ReadonlyMap<LaunchableCrm, CrmSyncedField> => {
    const fields = fieldsByQuestionId.get(questionId) ?? [];
    return new Map(
      connectedCrms.flatMap(crm => {
        const field = fields.find(candidate => candidate.integration === crm);
        return field ? [[crm, field]] : [];
      })
    );
  }, [connectedCrms, fieldsByQuestionId, questionId]);
  const [updateExtractionQuestion, { loading }] = Apollo.useUpdateExtractionQuestionMutation();
  const [pendingSelection, setPendingSelection] = useState<CrmSyncSelection | null>(null);

  const isSyncToCrmSaved = Boolean(extractionQuestion?.syncToCrm);
  const savedIntegrations = extractionQuestion?.syncToCrmIntegrations;
  const selection = useMemo(
    (): CrmSyncSelection =>
      pendingSelection ?? {
        syncToCrm: isSyncToCrmSaved,
        syncToCrmIntegrations: savedIntegrations ?? [],
      },
    [pendingSelection, savedIntegrations, isSyncToCrmSaved]
  );
  const selectedCrms = connectedCrms.filter(
    crm => selection.syncToCrm || selection.syncToCrmIntegrations.includes(crm)
  );
  const hasFieldNotLive = selectedCrms.some(
    crm => fieldByCrm.get(crm)?.status !== Apollo.CrmProvisionedFieldStatus.Live
  );

  useEffect(() => {
    if (!isOpen || !hasFieldNotLive) return;
    startPolling(CRM_SETUP_POLL_MS);
    return stopPolling;
  }, [hasFieldNotLive, isOpen, startPolling, stopPolling]);

  const save = useCallback(
    async (next: CrmSyncSelection) => {
      const refetchVariables: Apollo.GetExtractionQuestionWithInputsQueryVariables = {
        id: questionId,
      };
      let isErrorHandled = false;
      const context: OperationErrorContext = {
        genericErrorMessage: 'Failed to update CRM sync. Please try again.',
        onErrorHandled: () => {
          isErrorHandled = true;
        },
      };
      setPendingSelection(next);
      try {
        await updateExtractionQuestion({
          awaitRefetchQueries: true,
          context,
          refetchQueries: [
            { query: Apollo.GetExtractionQuestionWithInputsDocument, variables: refetchVariables },
            Apollo.GetCrmProvisionedFieldsDocument,
          ],
          variables: {
            input: {
              props: {
                syncToCrm: next.syncToCrm,
                syncToCrmIntegrations: [...next.syncToCrmIntegrations],
              },
              questionId,
            },
          },
        });
        enqueueSnackbar(getSavedMessage(next), { variant: 'success' });
      } catch {
        if (!isErrorHandled)
          enqueueSnackbar('Failed to update CRM sync. Please try again.', { variant: 'error' });
      } finally {
        setPendingSelection(null);
      }
    },
    [enqueueSnackbar, questionId, updateExtractionQuestion]
  );

  const setAll = useCallback(
    (checked: boolean) =>
      save({
        syncToCrm: checked,
        syncToCrmIntegrations: checked ? selection.syncToCrmIntegrations : connectedCrms,
      }),
    [connectedCrms, save, selection.syncToCrmIntegrations]
  );

  const setCrm = useCallback(
    (crm: LaunchableCrm, checked: boolean) => {
      const current = selection.syncToCrm ? connectedCrms : selection.syncToCrmIntegrations;
      const others = current.filter(integration => integration !== crm);
      return save({
        syncToCrm: false,
        syncToCrmIntegrations: checked ? [...others, crm] : others,
      });
    },
    [connectedCrms, save, selection.syncToCrm, selection.syncToCrmIntegrations]
  );

  return { connectedCrms, fieldByCrm, loading, selectedCrms, selection, setAll, setCrm };
}

function getSavedMessage(selection: CrmSyncSelection): string {
  const names = selection.syncToCrmIntegrations
    .filter(getIsLaunchableCrm)
    .map(crm => INTEGRATION_DISPLAY_NAME[crm]);
  if (!selection.syncToCrm && names.length === 0) return 'Stopped syncing to CRMs.';
  const target = selection.syncToCrm
    ? 'all CRMs, including ones you connect later'
    : names.join(' and ');
  return `Syncing to ${target}. You'll get a notification if anything needs your attention.`;
}

interface SyncToCrmListProps {
  connectedCrms: readonly LaunchableCrm[];
  disabled: boolean;
  fieldByCrm: ReadonlyMap<LaunchableCrm, CrmSyncedField>;
  isAll: boolean;
  onRecheck: (crm: LaunchableCrm) => void;
  onSetAll: (checked: boolean) => void;
  onSetCrm: (crm: LaunchableCrm, checked: boolean) => void;
  recheckLoading: boolean;
  search: string;
  selectedCrms: readonly LaunchableCrm[];
}

function SyncToCrmList({
  connectedCrms,
  disabled,
  fieldByCrm,
  isAll,
  onRecheck,
  onSetAll,
  onSetCrm,
  recheckLoading,
  search,
  selectedCrms,
}: SyncToCrmListProps): JSX.Element {
  const { classes } = useCrmSyncStyles();
  const searchedCrms = matchSorter([...connectedCrms], search, {
    keys: [crm => INTEGRATION_DISPLAY_NAME[crm]],
  });

  return (
    <MenuList data-cy="sync-to-crm-popper">
      <MenuItem
        className={classes.item}
        data-cy="sync-to-crm-option-all"
        disabled={disabled}
        onClick={() => onSetAll(!isAll)}
      >
        <AlineCheckbox checked={isAll} classes={{ root: classes.checkbox }} />
        <div className={classes.iconSlot}>
          <Layers className={classes.icon} />
        </div>
        <Typography noWrap>All CRMs</Typography>
      </MenuItem>
      <Divider />
      <div className={classes.item}>
        <Typography className={classes.sectionLabel} variant="caption">
          Connected CRMs
        </Typography>
      </div>
      {searchedCrms.length === 0 && (
        <div className={classes.item}>
          <Typography>No CRMs match your search.</Typography>
        </div>
      )}
      {searchedCrms.map(crm => {
        const isSelected = selectedCrms.includes(crm);
        return (
          <MenuItem
            key={crm}
            className={classes.item}
            data-cy={`sync-to-crm-option-${crm}`}
            disabled={disabled}
            onClick={() => onSetCrm(crm, !isSelected)}
          >
            <AlineCheckbox checked={isSelected} classes={{ root: classes.checkbox }} />
            <div className={classes.iconSlot}>
              <CrmIntegrationIcon className={classes.icon} integration={crm} />
            </div>
            <Typography noWrap>{INTEGRATION_DISPLAY_NAME[crm]}</Typography>
            {isSelected && (
              <FieldStatus
                crm={crm}
                field={fieldByCrm.get(crm) ?? null}
                recheckLoading={recheckLoading}
                onRecheck={onRecheck}
              />
            )}
          </MenuItem>
        );
      })}
    </MenuList>
  );
}

function FieldStatus({
  crm,
  field,
  onRecheck,
  recheckLoading,
}: {
  readonly crm: LaunchableCrm;
  readonly field: CrmSyncedField | null;
  readonly onRecheck: (crm: LaunchableCrm) => void;
  readonly recheckLoading: boolean;
}): JSX.Element {
  const { classes, cx } = useCrmSyncStyles();
  const hasField = Boolean(field);
  const [isSetupSlow, setIsSetupSlow] = useState(false);

  useEffect(() => {
    if (hasField) return;
    const timeout = setTimeout(() => setIsSetupSlow(true), SETUP_RECHECK_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [hasField]);

  const statusClass: Record<Apollo.CrmProvisionedFieldStatus, string> = {
    [Apollo.CrmProvisionedFieldStatus.Failed]: classes.statusFailed,
    [Apollo.CrmProvisionedFieldStatus.Live]: classes.statusLive,
    [Apollo.CrmProvisionedFieldStatus.Pending]: classes.statusPending,
  };
  const canRecheck = field ? field.status !== Apollo.CrmProvisionedFieldStatus.Live : isSetupSlow;

  return (
    <>
      <Tooltip title={field?.detail ?? ''}>
        <Typography
          className={cx(
            classes.status,
            field ? statusClass[field.status] : classes.statusSettingUp
          )}
          data-cy={`sync-to-crm-status-${crm}`}
        >
          {field ? FIELD_STATUS_LABEL[field.status] : 'Setting up…'}
        </Typography>
      </Tooltip>
      {canRecheck && (
        <Tooltip title="Check again">
          <span>
            <IconButton
              aria-label={`Check ${INTEGRATION_DISPLAY_NAME[crm]} again`}
              className={classes.recheck}
              disabled={recheckLoading}
              size="small"
              onClick={event => {
                event.stopPropagation();
                onRecheck(crm);
              }}
            >
              <RefreshCw size={RECHECK_ICON_SIZE} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );
}

function CrmSyncSelectionIcons({
  isAll,
  selectedCrms,
}: {
  isAll: boolean;
  selectedCrms: readonly LaunchableCrm[];
}): JSX.Element {
  const { classes } = useCrmSyncStyles();
  const { classes: chipIconClasses } = useInputChipIconStyles();
  if (isAll)
    return (
      <InputChip
        icon={<Layers className={chipIconClasses.icon} />}
        label="All CRMs"
        title="All CRMs, including ones connected later"
      />
    );
  if (selectedCrms.length === 0)
    return <Typography className={classes.selectionText}>Off</Typography>;

  return (
    <div className={classes.chips}>
      {selectedCrms.map(crm => (
        <InputChip
          key={crm}
          icon={<CrmIntegrationIcon className={chipIconClasses.icon} integration={crm} />}
          label={INTEGRATION_DISPLAY_NAME[crm]}
        />
      ))}
    </div>
  );
}

function getSyncedCrmNames(
  question: Partial<Pick<Apollo.ExtractionQuestion, 'syncToCrm' | 'syncToCrmIntegrations'>>
): string | null {
  if (question.syncToCrm) return 'all your CRMs';
  const names = (question.syncToCrmIntegrations ?? [])
    .filter(getIsLaunchableCrm)
    .map(crm => INTEGRATION_DISPLAY_NAME[crm]);
  return names.length > 0 ? names.join(' and ') : null;
}

function SyncToCrmEmptyState({
  isOpen,
  onClose,
  onOpen,
  popperAnchor,
}: Pick<SyncToCrmRowProps, 'isOpen' | 'onClose' | 'onOpen' | 'popperAnchor'>): JSX.Element {
  const { classes, cx } = useQuestionMenuBodyStyles({ activeMenu: isOpen ? 'crmSync' : null });
  const { classes: crmClasses } = useCrmSyncStyles();
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <ButtonBase
        ref={setButtonEl}
        className={cx(classes.menuButton, classes.crmSyncMenuButton)}
        data-cy="sync-to-crm-menu-item"
        onClick={onOpen}
      >
        <Typography className={classes.buttonTitle}>Sync to CRMs</Typography>
        <Spacer />
        <Typography className={crmClasses.selectionText}>Not connected</Typography>
        <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
      </ButtonBase>
      <ColumnMenuPopper
        popperProps={{
          anchorEl: buttonEl,
          modifiers: CRM_SYNC_FLYOUT_MODIFIERS,
          open: isOpen,
          placement: popperAnchor,
        }}
        onClose={onClose}
      >
        <MenuList data-cy="sync-to-crm-empty">
          <div className={crmClasses.item}>
            <Typography className={crmClasses.sectionLabel} variant="caption">
              Connected CRMs
            </Typography>
          </div>
          <MenuItem
            className={cx(crmClasses.item, crmClasses.connectItem)}
            component={RouterLink}
            data-cy="sync-to-crm-connect"
            to={getIntegrationsSectionLink(INTEGRATIONS_SECTION.crms)}
            onClick={event => event.stopPropagation()}
          >
            <div className={crmClasses.iconSlot}>
              <Plus className={crmClasses.icon} />
            </div>
            <Typography>Connect CRM</Typography>
          </MenuItem>
        </MenuList>
      </ColumnMenuPopper>
    </>
  );
}

function SyncToCrmControl({
  canChange,
  isOpen,
  onClose,
  onOpen,
  popperAnchor,
  questionId,
}: SyncToCrmRowProps & { readonly canChange: boolean }): JSX.Element {
  const { classes, cx } = useQuestionMenuBodyStyles({ activeMenu: isOpen ? 'crmSync' : null });
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const isMenuOpen = isOpen && canChange;
  const { connectedCrms, fieldByCrm, loading, selectedCrms, selection, setAll, setCrm } =
    useCrmSyncSelection(questionId, isMenuOpen);
  const { loading: recheckLoading, recheck } = useRecheckCrmFields();

  return (
    <>
      <AlineTooltip arrow placement="top" title={canChange ? '' : ONLY_ADMINS_CAN_CHANGE_CRM_SYNC}>
        <div className={classes.tooltipWrapper}>
          <ButtonBase
            ref={setButtonEl}
            className={cx(classes.menuButton, classes.crmSyncMenuButton)}
            data-cy="sync-to-crm-menu-item"
            disabled={!canChange}
            onClick={() => {
              if (canChange) onOpen();
            }}
          >
            <Typography className={classes.buttonTitle}>Sync to CRMs</Typography>
            <Spacer />
            <CrmSyncSelectionIcons isAll={selection.syncToCrm} selectedCrms={selectedCrms} />
            <ChevronDown size={QUESTION_HEADER_CHEVRON_SIZE} />
          </ButtonBase>
        </div>
      </AlineTooltip>
      <ColumnMenuPopper
        popperProps={{
          anchorEl: buttonEl,
          modifiers: CRM_SYNC_FLYOUT_MODIFIERS,
          open: isMenuOpen,
          placement: popperAnchor,
        }}
        search={{ placeholder: 'Search CRMs...', setValue: setSearch, value: search }}
        onClose={() => {
          onClose();
          setSearch('');
        }}
      >
        <SyncToCrmList
          connectedCrms={connectedCrms}
          disabled={loading}
          fieldByCrm={fieldByCrm}
          isAll={selection.syncToCrm}
          recheckLoading={recheckLoading}
          search={search}
          selectedCrms={selectedCrms}
          onRecheck={recheck}
          onSetAll={checked => void setAll(checked)}
          onSetCrm={(crm, checked) => void setCrm(crm, checked)}
        />
      </ColumnMenuPopper>
    </>
  );
}
